import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, Download, Loader2, Undo2, PackageX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { getFobDamageReport, exportFobDamageCsv, returnFobDamageToSupplier } from '@/services/fobSectionApi';

const FOBDamageReport = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ total_usns: 0, total_pending: 0, total_groups: 0 });
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [status, setStatus] = useState('all');
  const [returnTarget, setReturnTarget] = useState(null);
  const [returning, setReturning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { status };
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const d = await getFobDamageReport(params);
      setRows(d.rows || []);
      setTotals({ total_usns: d.total_usns || 0, total_pending: d.total_pending || 0, total_groups: d.total_groups || 0 });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load damage report');
    } finally {
      setLoading(false);
    }
  }, [status, fromDate, toDate]);

  useEffect(() => { load(); }, [load]);

  const handleExport = async () => {
    try {
      const params = { status };
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const blob = await exportFobDamageCsv(params);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fob_damage_report_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch {
      toast.error('Failed to export CSV');
    }
  };

  const doReturn = async () => {
    if (!returnTarget) return;
    setReturning(true);
    try {
      const res = await returnFobDamageToSupplier({
        usn_codes: returnTarget.usns,
        vendor_name: returnTarget.vendor_name,
      });
      toast.success(`Returned ${res.total_usns} piece(s) to ${res.vendor_name} — slip ${res.slip_id}`);
      setReturnTarget(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to return to supplier');
    } finally {
      setReturning(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/sections/fob')} className="text-[#F9FAFB] hover:bg-white/10" data-testid="back-button">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <AlertTriangle className="w-6 h-6 text-red-400" />
          <div className="mr-auto">
            <h1 className="text-xl font-bold font-display">Damage Pieces Report</h1>
            <p className="text-sm text-[#E8DCC8]">FOB — damaged pieces by design / vendor, with supplier return</p>
          </div>
          <Button onClick={handleExport} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="damage-export-btn">
            <Download className="w-4 h-4 mr-2" />Export CSV
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        {/* Filters */}
        <div className="flex items-end gap-3 flex-wrap mb-4">
          <div>
            <Label className="text-xs">From date</Label>
            <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="h-9 w-40" data-testid="damage-from-date" />
          </div>
          <div>
            <Label className="text-xs">To date</Label>
            <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="h-9 w-40" data-testid="damage-to-date" />
          </div>
          <div>
            <Label className="text-xs">Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="h-9 w-40" data-testid="damage-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="pending">Pending return</SelectItem>
                <SelectItem value="returned">Returned</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button variant="ghost" className="h-9 text-[#5A5F6E]" onClick={() => { setFromDate(''); setToDate(''); setStatus('all'); }} data-testid="damage-clear-btn">Clear</Button>
          <div className="ml-auto flex gap-4 text-sm">
            <span className="text-[#5A5F6E]">Groups: <strong className="text-[#1A1F2E]" data-testid="damage-total-groups">{totals.total_groups}</strong></span>
            <span className="text-[#5A5F6E]">Damaged pieces: <strong className="text-red-700" data-testid="damage-total-usns">{totals.total_usns}</strong></span>
            <span className="text-[#5A5F6E]">Pending return: <strong className="text-amber-700" data-testid="damage-total-pending">{totals.total_pending}</strong></span>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-[#5A5F6E]"><Loader2 className="w-6 h-6 animate-spin mr-2" />Loading…</div>
        ) : rows.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-16 text-center text-[#5A5F6E]" data-testid="damage-empty">
              <PackageX className="w-12 h-12 mx-auto text-gray-400 mb-3" />
              No damaged pieces for this filter.
            </CardContent>
          </Card>
        ) : (
          <div className="bg-white rounded-lg border border-[#E5DFD0] overflow-x-auto" data-testid="damage-table">
            <table className="w-full text-sm">
              <thead className="bg-[#F5F3EC] text-[#5A5F6E]">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium">Design Name</th>
                  <th className="text-left px-3 py-2.5 font-medium">Shade</th>
                  <th className="text-left px-3 py-2.5 font-medium">Sort No</th>
                  <th className="text-left px-3 py-2.5 font-medium">Vendor</th>
                  <th className="text-left px-3 py-2.5 font-medium">Vendor Doc No</th>
                  <th className="text-left px-3 py-2.5 font-medium">Vendor Doc Date</th>
                  <th className="text-right px-3 py-2.5 font-medium">USNs</th>
                  <th className="text-right px-3 py-2.5 font-medium">Pending</th>
                  <th className="text-right px-3 py-2.5 font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-t border-[#E5DFD0] hover:bg-[#FBFAF5]" data-testid={`damage-row-${i}`}>
                    <td className="px-4 py-2.5 font-medium text-[#1A1F2E]">{r.design_name || '—'}</td>
                    <td className="px-3 py-2.5">{r.shade || '—'}</td>
                    <td className="px-3 py-2.5">{r.sort_no || '—'}</td>
                    <td className="px-3 py-2.5">{r.vendor_name || '—'}</td>
                    <td className="px-3 py-2.5">{r.vendor_doc_no || '—'}</td>
                    <td className="px-3 py-2.5">{r.vendor_doc_date || '—'}</td>
                    <td className="px-3 py-2.5 text-right font-semibold text-red-700">{r.usn_count}</td>
                    <td className="px-3 py-2.5 text-right">
                      {r.pending_count > 0
                        ? <span className="text-amber-700 font-semibold">{r.pending_count}</span>
                        : <span className="text-green-600 text-xs">returned</span>}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={r.pending_count === 0 || !r.vendor_name}
                        onClick={() => setReturnTarget(r)}
                        className="border-red-400 text-red-700 hover:bg-red-50"
                        data-testid={`damage-return-btn-${i}`}
                      >
                        <Undo2 className="w-4 h-4 mr-1" />Return
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {/* Return to supplier dialog */}
      <Dialog open={!!returnTarget} onOpenChange={(o) => { if (!o) setReturnTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="damage-return-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Undo2 className="w-5 h-5 text-red-600" />
              Return to Supplier
            </DialogTitle>
            <DialogDescription>
              Create a supplier return slip for <strong>{returnTarget?.pending_count}</strong> pending damaged
              piece(s) of <strong>{returnTarget?.design_name}</strong>
              {returnTarget?.shade ? ` / ${returnTarget.shade}` : ''} to vendor{' '}
              <strong>{returnTarget?.vendor_name}</strong>
              {returnTarget?.vendor_doc_no ? ` (doc ${returnTarget.vendor_doc_no})` : ''}. This appears in Supplier Returns.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReturnTarget(null)} data-testid="damage-return-cancel">Cancel</Button>
            <Button onClick={doReturn} disabled={returning} className="bg-red-600 hover:bg-red-700 text-white" data-testid="damage-return-confirm">
              {returning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Confirm return
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FOBDamageReport;
